import { useState, useCallback, useMemo } from 'react';
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  addDays, addWeeks, addMonths, parseISO,
} from 'date-fns';
import { todayStr, toStr } from '../utils/attendanceUtils';

const useAttendanceFilters = () => {
  const [scope, setScope] = useState('all');
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [empSearchQuery, setEmpSearchQuery] = useState('');
  const [period, setPeriod] = useState('today');
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);

  const applyPeriod = useCallback((p) => {
    const today = new Date();
    if (p === 'today') {
      const s = toStr(today);
      setDateFrom(s); setDateTo(s);
    } else if (p === 'week') {
      setDateFrom(toStr(startOfWeek(today, { weekStartsOn: 1 })));
      setDateTo(toStr(endOfWeek(today, { weekStartsOn: 1 })));
    } else if (p === 'month') {
      setDateFrom(toStr(startOfMonth(today)));
      setDateTo(toStr(endOfMonth(today)));
    }
  }, []);

  const handlePeriodChange = useCallback((p, resetResults) => {
    setPeriod(p);
    applyPeriod(p);
    if (resetResults) resetResults();
  }, [applyPeriod]);

  const navigate = useCallback((direction, resetResults) => {
    const from = parseISO(dateFrom);
    const to   = parseISO(dateTo);
    let newFrom, newTo;

    if (period === 'today') {
      newFrom = newTo = addDays(from, direction);
    } else if (period === 'week') {
      newFrom = addWeeks(from, direction);
      newTo   = addWeeks(to, direction);
    } else if (period === 'month') {
      newFrom = startOfMonth(addMonths(from, direction));
      newTo   = endOfMonth(addMonths(from, direction));
    } else {
      const delta = Math.round((to - from) / 86400000) + 1;
      newFrom = addDays(from, direction * delta);
      newTo   = addDays(to, direction * delta);
    }

    setDateFrom(toStr(newFrom));
    setDateTo(toStr(newTo));
    if (resetResults) resetResults();
  }, [dateFrom, dateTo, period]);

  const buildFilterParams = useCallback(() => {
    const params = {};
    if (scope === 'department' && selectedDeptId)
      params.department_id = parseInt(selectedDeptId);
    else if (scope === 'manual' && selectedEmpIds.length)
      params.employee_ids = selectedEmpIds.join(',');
    return params;
  }, [scope, selectedDeptId, selectedEmpIds]);

  const toggleEmployee = useCallback((id, resetResults) => {
    setSelectedEmpIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    if (resetResults) resetResults();
  }, []);

  return {
    scope, setScope,
    selectedDeptId, setSelectedDeptId,
    selectedEmpIds, setSelectedEmpIds,
    empSearchQuery, setEmpSearchQuery,
    period, setPeriod,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    applyPeriod,
    handlePeriodChange,
    navigate,
    buildFilterParams,
    toggleEmployee,
  };
};

export default useAttendanceFilters;
